import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo } from 'react'
import { ReplacementOption } from './ReplacementOption'
import { SelectionOption } from './SelectionOption'

/** Props for the OptionGroup component. */
interface OptionGroupProps {
    /** The content item to render */
    contentItem: Partial<TextContentElement>
    /** Flag that indicates whether the SelectionOptions are inactive */
    inactive?: boolean
    /** Whether the component is editable */
    editable?: boolean
}

/**
 * OptionGroup component Renders an option group of either SelectionOption or ReplacementOption
 * @param {OptionGroupProps} props - The properties for the OptionGroup component.
 * @returns {JSX.Element | null} The rendered option group or null if no valid elements are found.
 */
export const OptionGroup = memo<OptionGroupProps>(
    ({ contentItem, inactive = false, editable = false }) => {
        const { optionGroupContainer } = useOptionGroupStyles()
        const elements =
            contentItem.elements?.filter((e) => e.text?.trim()) ?? []

        if (elements.length === 0) return null

        return (
            <div
                data-testid={`option-group-container-${contentItem?.id}`}
                className={optionGroupContainer}
            >
                {elements.map((element) => {
                    const key = `option-${contentItem.id}-${
                        element.id ?? element.text
                    }`
                    if (element.type === NODE_TYPES.SelectionOption) {
                        return (
                            <SelectionOption
                                key={key}
                                contentItem={element}
                                inactive={inactive}
                                editable={editable}
                            />
                        )
                    }
                    return (
                        <ReplacementOption
                            key={key}
                            contentItem={element}
                            inactive={inactive}
                            editable={editable}
                        />
                    )
                })}
            </div>
        )
    }
)
